import { apiFetch } from '../../shared/api/apiClient';

export type PaymentEventProvider = 'braintree' | 'creem';

export interface PaymentEventItem {
  id: string;
  eventId: string;
  provider: PaymentEventProvider;
  kind: string;
  timestamp: string;
  processed: boolean;
  processingStatus: string;
  processedAt: string | null;
  processingAttempts: number;
  lastProcessingError: string | null;
  payloadPreview: string;
}

export interface PaymentEventsPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface PaymentEventsPage {
  events: PaymentEventItem[];
  pagination: PaymentEventsPagination;
}

export interface PaymentEventsFilters {
  provider?: PaymentEventProvider | '';
  kind?: string;
  processed?: 'true' | 'false' | '';
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

interface PaymentEventsResponseShape {
  events?: unknown;
  pagination?: unknown;
  error?: unknown;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function readBoolean(value: unknown): boolean {
  return value === true;
}

function readNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function readNullableString(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  return typeof value === 'string' ? value : null;
}

function readProvider(value: unknown): PaymentEventProvider {
  return value === 'creem' ? 'creem' : 'braintree';
}

function normalizeEvent(raw: unknown): PaymentEventItem {
  const r = isRecord(raw) ? raw : {};
  return {
    id: readString(r.id),
    eventId: readString(r.eventId),
    provider: readProvider(r.provider),
    kind: readString(r.kind),
    timestamp: readString(r.timestamp),
    processed: readBoolean(r.processed),
    processingStatus: readString(r.processingStatus),
    processedAt: readNullableString(r.processedAt),
    processingAttempts: readNumber(r.processingAttempts, 0),
    lastProcessingError: readNullableString(r.lastProcessingError),
    payloadPreview: readString(r.payloadPreview)
  };
}

function normalizePagination(raw: unknown, fallbackLimit: number): PaymentEventsPagination {
  const r = isRecord(raw) ? raw : {};
  return {
    page: readNumber(r.page, 1),
    limit: readNumber(r.limit, fallbackLimit),
    total: readNumber(r.total, 0),
    totalPages: readNumber(r.totalPages, 0)
  };
}

async function readJsonSafely(res: { json: () => Promise<unknown> }): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

export interface PaymentEventRetryResult {
  success: boolean;
  event: PaymentEventItem;
  message?: string;
}

/**
 * task-15: invoke the admin "Retry" endpoint to replay a failed/unprocessed
 * payment webhook event through the unified dispatcher. Resolves with the
 * updated event row on success and on handler-side failure (HTTP 502); only
 * rejects for transport errors or unexpected statuses (404/409/500). Callers
 * use the boolean `success` flag to decide whether to surface a green or red
 * toast in the UI.
 */
export async function retryPaymentEvent(eventId: string): Promise<PaymentEventRetryResult> {
  if (!eventId) throw new Error('Event ID is required to retry an event');
  const res = await apiFetch(`/api/webhooks/admin/events/${encodeURIComponent(eventId)}/retry`, { method: 'POST' });
  const body = await readJsonSafely(res);
  const rec = isRecord(body) ? body : {};
  // Handler success (200) and handler failure (502) both return the updated
  // row under `event` — surface it back so the UI can replace the table row.
  if (res.status === 200 || res.status === 502) {
    const event = normalizeEvent(rec.event);
    const message = readString(rec.message) || readString(rec.error) || undefined;
    return { success: res.status === 200, event, message };
  }
  // Other statuses (400/401/403/404/409/500) are control-plane errors —
  // surface the server's `error`/`message` to the caller so the UI shows it.
  const errMsg = readString(rec.error) || readString(rec.message) || `Retry failed with HTTP ${res.status}`;
  throw new Error(errMsg);
}

export async function fetchPaymentEvents(filters: PaymentEventsFilters = {}): Promise<PaymentEventsPage> {
  const qs = new URLSearchParams();
  if (filters.provider) qs.set('provider', filters.provider);
  if (filters.kind) qs.set('kind', filters.kind);
  if (filters.processed) qs.set('processed', filters.processed);
  if (filters.startDate) qs.set('startDate', filters.startDate);
  if (filters.endDate) qs.set('endDate', filters.endDate);
  if (filters.page) qs.set('page', String(filters.page));
  if (filters.limit) qs.set('limit', String(filters.limit));

  const url = `/api/webhooks/admin/events${qs.toString() ? `?${qs.toString()}` : ''}`;
  const res = await apiFetch(url);
  if (!res.ok) {
    let msg = 'Failed to fetch payment events';
    const body = await readJsonSafely(res);
    if (isRecord(body)) {
      const errMsg = readString((body as PaymentEventsResponseShape).error);
      if (errMsg) msg = errMsg;
    }
    throw new Error(msg);
  }
  const body = await readJsonSafely(res);
  const events = Array.isArray(isRecord(body) ? body.events : null)
    ? ((body as { events: unknown[] }).events).map(normalizeEvent)
    : [];
  const pagination = normalizePagination(isRecord(body) ? body.pagination : null, filters.limit ?? 25);
  return { events, pagination };
}

// task-19: append-only retry audit entry. Mirrors the backend
// `PaymentEventRetryEntry` shape so admin staff can see who clicked Retry,
// when, and what the outcome was without reading server logs.
export interface PaymentEventRetryActor {
  apiKeyMasked: string;
  apiKeyId?: string | null;
  apiKeyLabel?: string | null;
  tenantId?: string | null;
}

export interface PaymentEventRetryEntry {
  retriedAt: string;
  retriedBy: PaymentEventRetryActor;
  outcome: 'success' | 'failed';
  errorMessage?: string | null;
}

// task-16: detail shape returned by GET /api/webhooks/admin/events/:id
// Carries the FULL payload (no preview truncation) and the processing audit
// fields admins need to diagnose a single webhook from the dashboard.
// task-19: extends the contract with `retryHistory`.
export interface PaymentEventDetail extends PaymentEventItem {
  payload: unknown;
  result: unknown;
  createdAt: string | null;
  updatedAt: string | null;
  retryHistory: PaymentEventRetryEntry[];
}

// task-19: defensive normalizer for one retryHistory entry. We accept any
// shape from the wire so a bad row never crashes the modal; unknown fields
// degrade to safe defaults rather than throwing.
function normalizeRetryEntry(raw: unknown): PaymentEventRetryEntry {
  const r = isRecord(raw) ? raw : {};
  const actorRaw = isRecord(r.retriedBy) ? r.retriedBy : {};
  const outcome = r.outcome === 'success' ? 'success' : 'failed';
  return {
    retriedAt: readString(r.retriedAt),
    retriedBy: {
      apiKeyMasked: readString(actorRaw.apiKeyMasked) || 'unknown',
      apiKeyId: readNullableString(actorRaw.apiKeyId),
      apiKeyLabel: readNullableString(actorRaw.apiKeyLabel),
      tenantId: readNullableString(actorRaw.tenantId)
    },
    outcome,
    errorMessage: readNullableString(r.errorMessage)
  };
}

interface PaymentEventDetailResponseShape {
  event?: unknown;
  error?: unknown;
}

function readUnknown(value: unknown): unknown {
  return value === undefined ? null : value;
}

function normalizeDetail(raw: unknown): PaymentEventDetail {
  const r = isRecord(raw) ? raw : {};
  const history = Array.isArray(r.retryHistory) ? r.retryHistory.map(normalizeRetryEntry) : [];
  return {
    ...normalizeEvent(raw),
    // payloadPreview from the listing is empty here — the detail endpoint sends the full payload separately.
    payloadPreview: '',
    payload: readUnknown(r.payload),
    result: readUnknown(r.result),
    createdAt: readNullableString(r.createdAt),
    updatedAt: readNullableString(r.updatedAt),
    retryHistory: history
  };
}

export async function fetchPaymentEvent(id: string): Promise<PaymentEventDetail> {
  if (!id) throw new Error('Event id is required'); // explicit failure beats a misleading 404
  const url = `/api/webhooks/admin/events/${encodeURIComponent(id)}`;
  const res = await apiFetch(url);
  if (!res.ok) {
    let msg = 'Failed to fetch payment event';
    const body = await readJsonSafely(res);
    if (isRecord(body)) {
      const errMsg = readString((body as PaymentEventDetailResponseShape).error);
      if (errMsg) msg = errMsg;
    }
    throw new Error(msg);
  }
  const body = await readJsonSafely(res);
  const event = isRecord(body) ? (body as PaymentEventDetailResponseShape).event : null;
  return normalizeDetail(event);
}

// task-18: outcome status emitted by the bulk retry endpoint per id. Keep
// the literal union in lockstep with `RetryOutcomeStatus` in
// `features/webhooks/bulkRetryPaymentEventsCtrl.ts` so the UI's per-id
// rendering stays exhaustive against what the server actually returns.
export type BulkRetryStatus = 'retried' | 'failed' | 'skipped_processed' | 'not_found';

export interface BulkRetryResultItem {
  id: string;
  status: BulkRetryStatus;
  success: boolean;
  event?: PaymentEventItem;
  message?: string;
}

export interface BulkRetrySummary {
  total: number;
  succeeded: number;
  failed: number;
  skipped: number;
  notFound: number;
}

export interface BulkRetryResponse {
  success: boolean;
  summary: BulkRetrySummary;
  results: BulkRetryResultItem[];
}

function readBulkStatus(value: unknown): BulkRetryStatus {
  if (value === 'retried' || value === 'failed' || value === 'skipped_processed' || value === 'not_found') {
    return value;
  }
  return 'failed';
}

function normalizeBulkResultItem(raw: unknown): BulkRetryResultItem {
  const r = isRecord(raw) ? raw : {};
  const item: BulkRetryResultItem = {
    id: readString(r.id),
    status: readBulkStatus(r.status),
    success: readBoolean(r.success)
  };
  if (isRecord(r.event)) item.event = normalizeEvent(r.event);
  const msg = readString(r.message);
  if (msg) item.message = msg;
  return item;
}

function normalizeBulkSummary(raw: unknown): BulkRetrySummary {
  const r = isRecord(raw) ? raw : {};
  return {
    total: readNumber(r.total, 0),
    succeeded: readNumber(r.succeeded, 0),
    failed: readNumber(r.failed, 0),
    skipped: readNumber(r.skipped, 0),
    notFound: readNumber(r.notFound, 0)
  };
}

/**
 * task-18: replay a batch of failed payment webhook events. Caller MUST
 * chunk large selections (the server caps each request at 100 ids) — this
 * client deliberately does NOT auto-chunk so callers stay aware of the per-
 * request boundary, which is what drives the UI's progress updates.
 *
 * Resolves on HTTP 200 with the parsed summary + per-id results. Throws on
 * client errors (400/401/403) or unexpected statuses so the UI can surface
 * the message instead of silently treating the whole batch as failed.
 */
export async function bulkRetryPaymentEvents(ids: string[]): Promise<BulkRetryResponse> {
  if (!Array.isArray(ids) || ids.length === 0) {
    throw new Error('At least one event id is required to bulk retry');
  }
  const res = await apiFetch('/api/webhooks/admin/events/retry-bulk', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids })
  });
  const body = await readJsonSafely(res);
  const rec = isRecord(body) ? body : {};
  if (res.status === 200) {
    const summary = normalizeBulkSummary(rec.summary);
    const results = Array.isArray(rec.results) ? rec.results.map(normalizeBulkResultItem) : [];
    return { success: readBoolean(rec.success), summary, results };
  }
  const errMsg = readString(rec.error) || readString(rec.message) || `Bulk retry failed with HTTP ${res.status}`;
  throw new Error(errMsg);
}

/**
 * task-18: walk every page of the listing endpoint, accumulating the row
 * `id`s of unprocessed events that match the given filters. Used by the
 * "Select all matching filter" action so staff can retry a whole incident's
 * worth of failures without scrolling through pages.
 *
 * Caps total scanned rows so a misconfigured filter cannot fetch the entire
 * collection. Returns the discovered ids plus a `truncated` flag the UI
 * surfaces to staff so they know the selection was bounded.
 */
export async function fetchAllUnprocessedIds(
  filters: PaymentEventsFilters = {},
  options: { maxIds?: number; pageSize?: number } = {}
): Promise<{ ids: string[]; truncated: boolean; total: number }> {
  const maxIds = options.maxIds ?? 1000;
  const pageSize = Math.min(options.pageSize ?? 200, 200); // server caps at 200
  const ids: string[] = [];
  let page = 1;
  let total = 0;
  while (ids.length < maxIds) {
    const result = await fetchPaymentEvents({
      ...filters,
      processed: 'false', // server-side filter — only unprocessed are eligible for retry
      page,
      limit: pageSize
    });
    total = result.pagination.total;
    for (const ev of result.events) {
      const rowId = ev.id || ev.eventId;
      if (!rowId) continue;
      ids.push(rowId);
      if (ids.length >= maxIds) break;
    }
    if (result.events.length < pageSize) break; // last page reached
    if (page >= result.pagination.totalPages) break;
    page += 1;
  }
  return { ids, truncated: total > ids.length, total };
}

export default { fetchPaymentEvents, fetchPaymentEvent, retryPaymentEvent, bulkRetryPaymentEvents, fetchAllUnprocessedIds };
